import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import SmartStaysLogo from '../components/SmartStaysLogo';
import PhoneInput from '../components/PhoneInput';
import { parsePhone, DEFAULT_PHONE_COUNTRY } from '../utils/phone';
import API_URL from '../config';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './Activate.css';
import '../publicTheme.css';

function Activate() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [personalCode, setPersonalCode] = useState('');
  const [confirmPersonalCode, setConfirmPersonalCode] = useState('');
  const [straat, setStraat] = useState('');
  const [huisnummer, setHuisnummer] = useState('');
  const [postcode, setPostcode] = useState('');
  const [stad, setStad] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('');

  useEffect(() => {
    document.title = 'SmartStays - Activeer account';

    const fetchUserRole = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/check-activation`, { token });
        setRole(response.data.role);
      } catch (error) {
        setMessage(t('activate.invalidLink'));
        setMessageType('error');
      }
    };
    fetchUserRole();
  }, [token, t]);

  const validateCode = (code) => {
    if (code.length === 0) return null;
    if (!/^[1-9]{6}$/.test(code)) {
      return t('activate.codeFormat');
    }
    if (code.startsWith('12')) {
      return t('activate.codeNoStart12');
    }
    return null;
  };

  const needsPersonalCode = role === 'SCHOONMAKER' || role === 'VERHUURDER';

  // De "ongeldig telefoonnummer"-melding verdwijnt zodra het nummer of land
  // wordt aangepast (anders blijft de rode balk staan terwijl het al klopt).
  const clearPhoneError = () => {
    if (!phoneInvalid) return;
    setPhoneInvalid(false);
    setMessage('');
    setMessageType('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateRequiredFields(e.target) || !firstName.trim() || !lastName.trim() || !password) {
      setMessage(t('activate.fillAll'));
      setMessageType('error');
      return;
    }

    if (password !== confirmPassword) {
      setMessage(t('activate.passwordsNoMatch'));
      setMessageType('error');
      return;
    }

    if (password.length < 6) {
      setMessage(t('activate.passwordMinLength'));
      setMessageType('error');
      return;
    }

    if (needsPersonalCode) {
      if (!personalCode || personalCode.length !== 6) {
        setMessage(t('activate.codeMustBe6'));
        setMessageType('error');
        return;
      }

      const codeError = validateCode(personalCode);
      if (codeError) {
        setMessage(codeError);
        setMessageType('error');
        return;
      }

      if (personalCode !== confirmPersonalCode) {
        setMessage(t('activate.codesNoMatch'));
        setMessageType('error');
        return;
      }

      if (!straat.trim() || !huisnummer.trim() || !postcode.trim() || !stad.trim() || !phone.trim()) {
        setMessage(t('activate.addressPhoneRequired'));
        setMessageType('error');
        return;
      }

      // Telefoonnummer moet juist gevormd zijn voor het gekozen land.
      if (!parsePhone(phone, phoneCountry).valid) {
        setPhoneInvalid(true);
        setMessage(t('activate.phoneInvalid'));
        setMessageType('error');
        return;
      }
    }

    setLoading(true);
    try {
      // Backend/database kennen enkel 1 gecombineerd "name"-veld (overal in
      // de app gebruikt voor weergave) - voornaam en achternaam worden hier
      // dus enkel voor het gebruiksgemak apart ingevuld en pas vlak voor het
      // versturen weer samengevoegd.
      const response = await axios.post(`${API_URL}/api/activate`, {
        token,
        name: `${firstName.trim()} ${lastName.trim()}`,
        password,
        personalCode: needsPersonalCode ? personalCode : null,
        straat: needsPersonalCode ? straat.trim() : null,
        huisnummer: needsPersonalCode ? huisnummer.trim() : null,
        postcode: needsPersonalCode ? postcode.trim() : null,
        stad: needsPersonalCode ? stad.trim() : null,
        phone: needsPersonalCode ? parsePhone(phone, phoneCountry).formatted : null
      });

      setMessage(response.data.message);
      setMessageType('success');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || t('activate.failed'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handlePersonalCodeChange = (e) => {
    const value = e.target.value.replace(/[^1-9]/g, '').slice(0, 6);
    setPersonalCode(value);
  };

  const handleConfirmPersonalCodeChange = (e) => {
    const value = e.target.value.replace(/[^1-9]/g, '').slice(0, 6);
    setConfirmPersonalCode(value);
  };

  const roleLabels = {
    SCHOONMAKER: t('activate.roleCleaner'),
    VERHUURDER: t('activate.roleOwner'),
  };

  return (
    <div className="activate-container">
      <div className="activate-card">
        <div className="activate-header">
          <h1><SmartStaysLogo /></h1>
          <p>{t('activate.subtitle', { role: roleLabels[role] || '' })}</p>
        </div>

        <form onSubmit={handleSubmit} noValidate onInput={clearFieldInvalid}>
          <div className="form-row">
            <div className="form-group form-group-wide">
              <label>{t('activate.firstName')}</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="form-group form-group-wide">
              <label>{t('activate.lastName')}</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('activate.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('activate.repeatPassword')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          {needsPersonalCode && (
            <>
              <div className="form-group">
                <label>{t('activate.personalCode')}</label>
                <input
                  type="text"
                  value={personalCode}
                  onChange={handlePersonalCodeChange}
                  required
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
                <small className="code-hint">
                  {role === 'SCHOONMAKER'
                    ? t('activate.codeHintCleaner')
                    : t('activate.codeHintOwner')}
                </small>
              </div>

              <div className="form-group">
                <label>{t('activate.repeatPersonalCode')}</label>
                <input
                  type="text"
                  value={confirmPersonalCode}
                  onChange={handleConfirmPersonalCodeChange}
                  required
                  maxLength="6"
                  className="code-input"
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                />
              </div>

              <div className="form-row">
                <div className="form-group form-group-wide">
                  <label>{t('activate.straat')}</label>
                  <input
                    type="text"
                    value={straat}
                    onChange={(e) => setStraat(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group form-group-narrow">
                  <label>{t('activate.huisnummer')}</label>
                  <input
                    type="text"
                    value={huisnummer}
                    onChange={(e) => setHuisnummer(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-narrow">
                  <label>{t('activate.postcode')}</label>
                  <input
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group form-group-wide">
                  <label>{t('activate.stad')}</label>
                  <input
                    type="text"
                    value={stad}
                    onChange={(e) => setStad(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('activate.phone')}</label>
                <PhoneInput
                  country={phoneCountry}
                  value={phone}
                  onCountryChange={(c) => { setPhoneCountry(c); clearPhoneError(); }}
                  onValueChange={(v) => { setPhone(v); clearPhoneError(); }}
                  invalid={phoneInvalid}
                  required
                />
              </div>
            </>
          )}

          {message && (
            <div className={`message ${messageType}`}>
              {message}
            </div>
          )}

          <button type="submit" disabled={loading}>
            {loading ? t('activate.loading') : t('activate.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Activate;
